import { Box, lighten, Skeleton, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from 'recharts';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getCountMembersByMonth } from 'src/shared/services/memberService';
import { Colors } from 'src/shared/components/styles/colors';
import { ChartFailMessage } from '../atoms/chartFailMessage';
import { ChartInfoTooltip } from '../atoms/chartInfoTooltip';
import { HeaderSubTitle } from '../atoms/headerSubTitle';

// Samma toner som ColoredBox-widgeten ("Medlemmar"-kortet) — pastellig grön/rose
const PAID_COLOR = lighten(Colors.ChipColors.Green, 0.4);
const UNPAID_COLOR = lighten(Colors.ChipColors.Red, 0.4);

type MemberChartV2Props = {
  associationId: number | undefined;
  districtId: number | undefined;
};

type ChartRow = {
  label: string;
  paid: number;
  unpaid: number;
};

const ChartTooltip = ({
  active,
  payload,
  label,
  paidLabel,
  unpaidLabel,
}: TooltipProps<number, string> & { paidLabel: string; unpaidLabel: string }) => {
  if (!active || !payload?.length) return null;
  const paid = payload.find((p) => p.dataKey === 'paid')?.value ?? 0;
  const unpaid = payload.find((p) => p.dataKey === 'unpaid')?.value ?? 0;
  return (
    <Box
      sx={{
        bgcolor: 'rgba(29,28,26,0.92)',
        color: '#fff',
        px: 1.25,
        py: 0.75,
        borderRadius: 1,
        fontSize: 12,
        fontWeight: 500,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      }}
    >
      <Box sx={{ fontWeight: 600, mb: 0.25 }}>{label}</Box>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Box sx={{ color: PAID_COLOR }}>
          {paidLabel}: {paid}
        </Box>
        <Box sx={{ color: UNPAID_COLOR }}>
          {unpaidLabel}: {unpaid}
        </Box>
      </Box>
    </Box>
  );
};

export const MemberChartV2 = (props: MemberChartV2Props) => {
  const { associationId, districtId } = props;
  const { t } = useTranslation(['dashboard']);
  const theme = useTheme();
  const isMounted = useIsMounted();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [data, setData] = useState<ChartRow[]>([]);

  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
    const fetchData = async () => {
      try {
        const result = await getCountMembersByMonth(
          dayjs(new Date()).add(-12, 'month').startOf('month').format('YYYY-MM-DD'),
          dayjs(new Date()).format('YYYY-MM-DD'),
          associationId,
          districtId,
        );
        if (isMounted()) {
          const rows: ChartRow[] = result.data.map((x) => ({
            label: `${x.month} ${String(x.year).slice(2)}`,
            paid: x.paid,
            unpaid: x.unpaid,
          }));
          setData(rows);
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.error(e);
          setHasError(true);
        }
      } finally {
        setTimeout(() => {
          if (isMounted()) {
            setIsLoading(false);
          }
        }, 300);
      }
    };
    fetchData();
  }, [isMounted, associationId, districtId]);

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <HeaderSubTitle label={t('dashboard:members.chartHeader')} />
          <ChartInfoTooltip />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, fontSize: 12, color: theme.palette.text.secondary }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: PAID_COLOR }} />
            {t('dashboard:members.chart.paid')}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: UNPAID_COLOR }} />
            {t('dashboard:members.chart.unpaid')}
          </Box>
        </Box>
      </Box>

      {isLoading && <Skeleton variant='rectangular' animation='wave' sx={{ flex: 1, minHeight: 240, borderRadius: 1 }} />}

      {!isLoading && !hasError && data.length > 0 && (
        <Box sx={{ width: '100%', flex: 1, minHeight: 0 }}>
          <ResponsiveContainer width='100%' height='100%'>
            <BarChart data={data} margin={{ top: 12, right: 16, left: 0, bottom: 8 }} barCategoryGap='25%'>
              <CartesianGrid stroke='#ebe9e3' strokeDasharray='2 3' vertical={false} />
              <XAxis
                dataKey='label'
                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                axisLine={false}
                tickLine={false}
                interval='preserveStartEnd'
              />
              <YAxis tick={{ fontSize: 11, fill: theme.palette.text.secondary }} axisLine={false} tickLine={false} width={40} allowDecimals={false} />
              <Tooltip
                cursor={{ fill: 'rgba(0,68,126,0.06)' }}
                content={
                  <ChartTooltip paidLabel={t('dashboard:members.chart.paid') as string} unpaidLabel={t('dashboard:members.chart.unpaid') as string} />
                }
              />
              <Bar dataKey='paid' fill={PAID_COLOR} radius={[6, 6, 0, 0]} />
              <Bar dataKey='unpaid' fill={UNPAID_COLOR} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}

      {hasError && <ChartFailMessage />}
    </Box>
  );
};
